import {
    IInsuranceConfiguration,
    IInsuranceConfigurationViewModel,
    IInsuranceProductConfiguration,
    IInsuranceProductConfigurationViewModel,
    IInsuranceProviderConfiguration, IInsuranceProviderConfigurationViewModel
} from "./insurance-configuration.interfaces";
import {IServiceFactory} from "../../service-factory.interface";
import {Check} from "../../../types/type-checking";
import {NullableString} from "../../../types/nullable-types";

export class InsuranceProductConfigurationModel implements IInsuranceProductConfigurationViewModel {
    constructor(private readonly config: IInsuranceProductConfiguration,
                private readonly services: IServiceFactory) {
        this.feeTranslations = this._parseFeeTranslations(this.config.feeTranslationsJson);
    }

    readonly feeTranslations: Record<string, string> = {};

    get code(): string {
        return this.config.code;
    }

    get ssrCode(): string {
        return this.config.ssrCode?.toUpperCase() ?? "";
    }

    get feeCode(): string {
        return this.config.feeCode?.toUpperCase() ?? "";
    }

    private _parseFeeTranslations(translationsJson: string): Record<string, string> {
        if(Check.isEmpty(translationsJson)) {
            return {};
        }
        try {
            const result: Record<string, string> = {};
            const parsedJson = JSON.parse(translationsJson);
            for(const lang of Object.keys(parsedJson)) {
                result[lang.toLowerCase()] = parsedJson[lang];
            }
            return result;
        } catch(err) {
            this.services.logger.error(`Error parsing insurance fee translations for product ${this.config.code}`, err, translationsJson);
            return {};
        }

    }

}

export class InsuranceProviderConfigurationModel implements IInsuranceProviderConfigurationViewModel{
    constructor(private readonly config: IInsuranceProviderConfiguration,
                services: IServiceFactory) {
        this.products = config.products.map(p => new InsuranceProductConfigurationModel(p, services));
    }

    get code(): string {
        return this.config.code;
    }

    get isEnabled(): boolean {
        return this.config.isEnabled;
    }

    readonly products: InsuranceProductConfigurationModel[];

    get specificConfigs(): Record<string, string> {
        return this.config.specificConfigs ?? {};
    }

    getAllSsrsCodes(): string[] {
        return this.products.map(p => p.ssrCode);
    }

    getAllFeesTranslations(): Record<string, Record<string, string>> {
        const result: Record<string, Record<string, string>> = {};
        for(const product of this.products) {
            if(!result[product.feeCode]) {
                result[product.feeCode] = {};
            }

            Object.keys(product.feeTranslations).forEach(lang => {
                result[product.feeCode][lang] = product.feeTranslations[lang];
            })
        }
        return result;
    }
}

export class InsuranceConfigurationModel implements IInsuranceConfigurationViewModel {

    constructor(private readonly insuranceConfig: IInsuranceConfiguration, private readonly services: IServiceFactory) {
        for(const providerConfig of (insuranceConfig?.providers ?? [])) {
            this._providers[providerConfig.code.toUpperCase()] = new InsuranceProviderConfigurationModel(providerConfig, services);
        }
        const providersList = Object.values(this._providers);

        for(const provider of providersList) {
            this._allSsrsCodes = [
                ...this._allSsrsCodes,
                ...provider.getAllSsrsCodes()
            ]

            this._allFeesTranslations = {
                ...this._allFeesTranslations,
                ...provider.getAllFeesTranslations()
            }
        }
    }

    get isEnabled(): boolean {
        return Boolean(this.insuranceConfig?.enabled);
    }

    private _providers: Record<string, InsuranceProviderConfigurationModel> = {};
    private readonly _allSsrsCodes: string[] = [];
    private readonly _allFeesTranslations: Record<string, Record<string, string>> = {};


    hasSsrCode(ssrCode: string): boolean {
        return this._allSsrsCodes.includes(ssrCode.toUpperCase());
    }

    getFeeTranslation(feeCode: string): NullableString {
        feeCode = feeCode.toUpperCase();
        const lang = this.services.language.currentLanguage.toLowerCase();

        const feeTranslations = this._allFeesTranslations[feeCode];
        if(!feeTranslations) {
            return null;
        }

        return feeTranslations[lang] ?? null;
    }

    /*
    getProviderConfig(providerCode: string): IInsuranceProviderConfigurationViewModel | null {
        return this._providers[providerCode?.toUpperCase()] ?? null;
    }

     */

    getAllProvidersConfigs(): IInsuranceProviderConfigurationViewModel[] {
        return Object.values(this._providers);
    }



}